import { Group, Rect, Text } from "react-konva";
import { useAppSelector } from "@store/hooks";
import {
  selectCanvasDiagnostics,
  selectCategories,
} from "@store/selectors";
import {
  canvasTextFontFamily,
  canvasTextFontSize,
  charLimit,
} from "@shared/utils/consts";
import type { Category } from "@/types/models";

// Local helper — mirrors findCategoryColor in CanvasRectangle.tsx
// (not exported there). Returns the background color for a rect's
// category, or undefined when no match.
const findCategoryColor = (
  categories: Category[],
  rect: { category?: string }
): string | undefined =>
  categories.find((c) => c.label === rect.category)?.background;

/**
 * Stage 6.5: Konva overlay that renders expert canvas rectangles the
 * student didn't match. Each ghost is drawn at the expert's saved
 * (x, y) position as a faded dashed-stroke shape with `listening:
 * false` so it doesn't intercept canvas events.
 *
 * Mounted in Canvas.tsx alongside the regular rectangle layer. Renders
 * null when no expert reference exists or there are no missed experts.
 */
const CanvasGhostLayer = () => {
  const diagnostics = useAppSelector(selectCanvasDiagnostics);
  const categories = useAppSelector(selectCategories);

  if (!diagnostics.active || diagnostics.ghostExpertRects.length === 0) {
    return null;
  }

  return (
    <Group listening={false}>
      {diagnostics.ghostExpertRects.map((rect, i) => {
        const text = rect.text ?? "";
        const truncated =
          text.slice(0, charLimit) + (text.length > charLimit ? "…" : "");
        const categoryColor =
          findCategoryColor(categories, rect) || "#ddcc77";
        return (
          <Group key={`ghost-${i}`} x={rect.x} y={rect.y} opacity={0.6}>
            <Rect
              width={rect.width}
              height={rect.height}
              stroke={categoryColor}
              strokeWidth={2.5}
              dash={[8, 6]}
              fillEnabled={false}
              cornerRadius={5}
            />
            {text && (
              <Text
                text={truncated}
                align="left"
                width={rect.width}
                padding={6}
                fontFamily={canvasTextFontFamily}
                fontSize={canvasTextFontSize}
                fill={categoryColor}
              />
            )}
          </Group>
        );
      })}
    </Group>
  );
};

export default CanvasGhostLayer;
